import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { baseOption, axeValeur, TAILLE_TEXTE, COULEUR_TEXTE } from './echartsTheme';
import { ajouterJours, formatCourt, formatLong, formatNombre, isoDepuisTs } from '../utils/eau3mois';

// Palette du Shiny (05_helpers_graphiques.R).
export const COL_TEAL = '#0E7C86';
export const COL_IQ = '#7FB2B8';
export const COL_MINMAX = '#D6E4E6';
export const COL_MED = '#B3261E';
export const COL_OBS = '#1A1A1A';
export const COL_REEL = '#1565C0';

const INVISIBLE = { lineStyle: { opacity: 0 }, areaStyle: { opacity: 0 }, symbol: 'none', silent: true };

/**
 * Enveloppe des trajectoires (min–max, q25–q75, médiane) au fil de la campagne,
 * avec la partie observée (SAFRAN 2026) et le réalisé. Sert au Besoin journalier
 * ET au Cumul territorial : seules l'unité et le nombre de décimales changent.
 *
 * @param origine  date du jour 0 (AAAA-MM-JJ)
 * @param bandes   { min, q25, med, q75, max } — un tableau par statistique
 * @param nObs     nombre de jours observés (jusqu'à la coupure incluse)
 * @param realise  { origine, valeurs } | null — réalisé SAFRAN superposé
 * @param coupure  date de coupure SAFRAN -> DRIAS
 * @param unite    libellé de l'axe Y (posé au-dessus de l'axe)
 */
export default function Eau3MoisBandesChart({ origine, bandes, nObs, realise, coupure, unite, decimales = 2 }) {
  const n = bandes.med.length;
  const dates = Array.from({ length: n }, (_, i) => ajouterJours(origine, i));
  const paires = (valeurs, debut = origine) => valeurs.map((v, i) => [ajouterJours(debut, i), v]);

  const series = [
    { ...INVISIBLE, name: 'base-mm', type: 'line', stack: 'mm', data: paires(bandes.min) },
    {
      name: 'min – max', type: 'line', stack: 'mm', symbol: 'none', silent: true,
      lineStyle: { opacity: 0 }, areaStyle: { color: COL_MINMAX },
      data: paires(bandes.max.map((v, i) => v - bandes.min[i])),
    },
    { ...INVISIBLE, name: 'base-iq', type: 'line', stack: 'iq', data: paires(bandes.q25) },
    {
      name: 'q25 – q75', type: 'line', stack: 'iq', symbol: 'none', silent: true,
      lineStyle: { opacity: 0 }, areaStyle: { color: COL_IQ },
      data: paires(bandes.q75.map((v, i) => v - bandes.q25[i])),
    },
    {
      name: 'médiane des projections', type: 'line', symbol: 'none',
      lineStyle: { color: COL_MED, width: 2 }, itemStyle: { color: COL_MED },
      data: paires(bandes.med),
      markLine: {
        silent: true, symbol: 'none', label: { show: false },
        lineStyle: { type: 'dashed', color: '#595959' },
        data: [{ xAxis: coupure }],
      },
    },
    {
      name: 'observé (SAFRAN 2026)', type: 'line', symbol: 'none',
      lineStyle: { color: COL_OBS, width: 2.5 }, itemStyle: { color: COL_OBS },
      data: paires(bandes.med.slice(0, nObs)),
    },
  ];
  if (realise?.valeurs?.length) {
    series.push({
      name: 'réalisé (SAFRAN)', type: 'line', symbol: 'none',
      lineStyle: { color: COL_REEL, width: 2.5, type: 'dashed' }, itemStyle: { color: COL_REEL },
      data: paires(realise.valeurs, realise.origine),
      // Point à la dernière date du réalisé : pour le besoin journalier d'un maïs,
      // les valeurs de septembre-octobre sont proches de zéro et la courbe se
      // confond avec l'axe, ce qui donnait l'impression qu'elle s'arrêtait plus tôt.
      markPoint: {
        silent: true, animation: false, symbol: 'circle', symbolSize: 9,
        itemStyle: { color: COL_REEL, borderColor: '#fff', borderWidth: 1.5 },
        label: { show: false },
        data: [{ coord: [ajouterJours(realise.origine, realise.valeurs.length - 1), realise.valeurs[realise.valeurs.length - 1]] }],
      },
    });
  }

  const option = {
    ...baseOption(),
    grid: { left: 12, right: 24, bottom: 64, top: 56, containLabel: true },
    legend: {
      bottom: 0,
      itemGap: 18,
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      data: ['min – max', 'q25 – q75', 'médiane des projections', 'observé (SAFRAN 2026)',
        ...(realise?.valeurs?.length ? ['réalisé (SAFRAN)'] : [])],
    },
    tooltip: {
      trigger: 'axis',
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      formatter: (params) => {
        const i = dates.indexOf(isoDepuisTs(params[0].axisValue));
        if (i < 0) return '';
        const f = (v) => formatNombre(v, decimales);
        const lignes = [
          `<b>${formatLong(dates[i])}</b>`,
          `<span style="color:${COL_MED}">●</span> médiane : ${f(bandes.med[i])}`,
          `q25 – q75 : ${f(bandes.q25[i])} – ${f(bandes.q75[i])}`,
          `min – max : ${f(bandes.min[i])} – ${f(bandes.max[i])}`,
        ];
        if (realise?.valeurs?.length) {
          const jr = Math.round((new Date(dates[i]) - new Date(realise.origine)) / 864e5);
          if (jr >= 0 && jr < realise.valeurs.length) {
            lignes.push(`<span style="color:${COL_REEL}">●</span> réalisé : ${f(realise.valeurs[jr])}`);
          }
        }
        return lignes.join('<br/>');
      },
    },
    xAxis: {
      type: 'time',
      axisLabel: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE, hideOverlap: true,
        formatter: (v) => formatCourt(isoDepuisTs(v)) },
      axisLine: { lineStyle: { color: COULEUR_TEXTE } },
    },
    yAxis: { ...axeValeur({ nom: unite }), scale: false },
    series,
  };

  return <ResponsiveECharts option={option} style={{ height: 560 }} />;
}
